import type { NextRouter } from 'next/router'
import type { NavGroup, NavLink } from 'src/@core/layouts/types'
import { handleURLQueries, resolveNavPath } from 'src/@core/layouts/utils'
import { isNavGroup } from './navGuards'

/**
 * Resolved path for the single-store dashboard hub only: `/app/restaurant/:numericId`.
 * Must NOT match global routes like `/app/restaurant/allocation` (same single-segment shape).
 */
function isRestaurantStoreHubResolvedPath(resolvedPath: string): boolean {
  const segment = resolvedPath.split('?')[0].replace(/\/$/, '')

  return /^\/app\/restaurant\/\d+$/.test(segment)
}

/**
 * Active state for primary nav (v2 rail). Mirrors core VerticalNavLink behaviour closely enough for wayfinding.
 */
export function isPrimaryNavLinkActive(router: NextRouter, resolvedPath: string): boolean {
  if (!resolvedPath) return false

  /** Strip trailing slash so nav config matches `asPath` / pathname (Next does not normalize trailing slashes). */
  const resolved = resolvedPath.split('?')[0].replace(/\/$/, '')
  const currentPath = router.asPath
  const pathSeg = currentPath.split('?')[0].replace(/\/$/, '')

  /**
   * Products: match only this nav item’s URL (exact or nested under it).
   * Avoids highlighting every pill when visiting any `/app/products/…` route.
   * `/app/products` alone is the rail root — matches any `/app/products/…` path.
   */
  if (resolved.startsWith('/app/products')) {
    const norm = (p: string) => p.split('?')[0].replace(/\/$/, '')
    const cur = norm(currentPath)
    const res = norm(resolvedPath)

    if (res === '/app/products') {
      return cur === '/app/products' || cur.startsWith('/app/products/')
    }

    return cur === res || cur.startsWith(`${res}/`)
  }

  /** Primary rail "Home" → master list `/app/restaurant` only — not `/app/restaurant/:id/…`. */
  if (resolved === '/app/restaurant') {
    return pathSeg === '/app/restaurant'
  }

  /** Primary rail "Customer Support" → `/app/customer-portal` and all nested routes. */
  if (resolved === '/app/customer-portal') {
    return pathSeg === '/app/customer-portal' || pathSeg.startsWith('/app/customer-portal/')
  }

  /**
   * SubNav "Restaurant Details" → `/app/restaurant/[id]`.
   * `handleURLQueries` uses `asPath.includes(path)`, which would stay true on every child route under the same store.
   * Only highlight the hub on the actual hub route (not menu-services, operations, calendar, …).
   */
  if (isRestaurantStoreHubResolvedPath(resolved)) {
    return router.pathname === '/app/restaurant/[id]'
  }

  if (router.pathname === resolvedPath || router.pathname === resolved) return true

  if (handleURLQueries(router, resolvedPath)) return true

  if (resolved === '/app/restaurant/allocation' && pathSeg.startsWith('/app/restaurant/allocation')) {
    return true
  }

  if (resolved === '/app/user-management' && pathSeg === '/app/user-management') {
    return true
  }

  if (resolved === '/app/user-management/profile' && pathSeg.startsWith('/app/user-management/profile')) {
    return true
  }

  if (resolved === '/app/user-management/list' && pathSeg.startsWith('/app/user-management/list')) {
    return true
  }

  if (resolved === '/app/user-management/changes' && pathSeg.startsWith('/app/user-management/changes')) {
    return true
  }

  return pathSeg === resolved || pathSeg.startsWith(`${resolved}/`)
}

/** True if the route matches the group landing path or any descendant link path. */
export function isNavGroupSubtreeActive(
  router: NextRouter,
  group: NavGroup,
  id: string | string[] | undefined
): boolean {
  const idNorm = Array.isArray(id) ? id[0] : id
  const pathSeg = router.asPath.split('?')[0].replace(/\/$/, '')
  const storeHref = idNorm ? `/app/restaurant/${idNorm}` : null

  /**
   * Primary rail "Restaurants" — stay highlighted for the whole single-store shell (`/app/restaurant/:id`, all nested pages).
   * (Children still match separately for allocation, reports, … when not in a store context.)
   */
  if (group.title === 'Restaurants' && storeHref && (pathSeg === storeHref || pathSeg.startsWith(`${storeHref}/`))) {
    return true
  }

  if (group.path) {
    const p = resolveNavPath(group.path, id)
    if (p && isPrimaryNavLinkActive(router, p)) return true
  }

  if (!group.children?.length) return false

  for (const ch of group.children) {
    if (isNavGroup(ch)) {
      if (isNavGroupSubtreeActive(router, ch, id)) return true
      continue
    }
    const link = ch as NavLink
    if (!link.path) continue
    const href = resolveNavPath(link.path, id)
    if (href && isPrimaryNavLinkActive(router, href)) return true
  }

  return false
}
